import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { DI_TOKENS } from '@constants';
import { useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { BuildingOfficeIcon, CaretRightIcon } from '@phosphor-icons/react';
import { AuthStore } from '@stores';

interface Props {
  fileCount: number;
  /** Localized category name when the list is filtered to one category. */
  categoryLabel?: string | null;
}

/**
 * Page header for the Document Hub: a breadcrumb (`Workspace › Document Hub`,
 * with the category appended when filtered) above the large title and the
 * file-count / tenant subline. Mirrors the Organization Profile header.
 */
export const DocumentHubHeader: FC<Props> = observer(
  ({ fileCount, categoryLabel }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const navigate = useNavigate();
    const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
    const tenantName = authStore.userProfile?.tenant?.name ?? '';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="textSM_regular" color="text.tertiary">
              {t('app.orgProfile.breadcrumbRoot')}
            </Typography>
            <CaretRightIcon size={12} color={theme.palette.text.disabled} />
            <Box
              component="button"
              type="button"
              onClick={() => navigate('/tenant-profile')}
              sx={{
                border: 0,
                background: 'transparent',
                p: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                typography: 'textSM_regular',
                color: 'text.tertiary',
                '&:hover': { color: 'text.secondary', textDecoration: 'underline' },
              }}
            >
              {t('app.orgProfile.title')}
            </Box>
            <CaretRightIcon size={12} color={theme.palette.text.disabled} />
            <Typography
              variant={categoryLabel ? 'textSM_regular' : 'textSM_semiBold'}
              color={categoryLabel ? 'text.tertiary' : 'text.secondary'}
            >
              {t('app.documentHub.breadcrumb')}
            </Typography>
            {categoryLabel && (
              <>
                <CaretRightIcon size={12} color={theme.palette.text.disabled} />
                <Typography variant="textSM_semiBold" color="text.secondary">
                  {categoryLabel}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="secondary"
              startIcon={<BuildingOfficeIcon size={16} />}
              onClick={() => navigate('/tenant-profile')}
              sx={{ textTransform: 'none' }}
            >
              {t('app.orgProfile.title')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="displaySM_bold" color="text.primary">
            {t('app.documentHub.title')}
          </Typography>
          <Typography variant="textMD_regular" color="text.tertiary">
            {t('app.documentHub.fileCount', { count: fileCount })}
            {tenantName ? ` · ${tenantName}` : ''}
          </Typography>
        </Box>
      </Box>
    );
  },
);
